import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PlayingCard from './PlayingCard';
import { sounds } from '@/audio/sounds';

/**
 * Multi-step Joker reveal overlay. Steps:
 *   0 — Joker card + JOKER! text (joker sting plays here)
 *   1 — Follow-up shot card
 *   2 — Shot card VS goalkeeper side-by-side
 *   3 — Shot card + GK still visible + GOAL!/SAVE! result text (sound here)
 *   4 — close (onDone)
 *
 * Tap-to-skip CONTRACT (per 2026-02-12 part 5 spec):
 *   On skip, the FINAL result frame must always show
 *     • the shot card
 *     • the goalkeeper card (or "EMPTY GOAL" slot)
 *     • the GOAL / SAVE text
 *   ...simultaneously, for at least MIN_RESULT_MS (1000 ms). Skipping never
 *   bypasses the cards — only the lead-in steps are fast-forwarded.
 */
const RESULT_STEP = 3;
const TOTAL_STEPS = 4;
const MIN_RESULT_MS = 1000;
/** Compensates for AnimatePresence mode="wait" exit duration (~300-350 ms) on
 * the previous step. Ensures the final result frame stays visually mounted
 * for the full MIN_RESULT_MS after skip. */
const SKIP_LEAD_IN_MS = 400;
const DURATIONS = [1400, 1200, 1500, 1600];

function GkOrEmpty({ gk, size = 78 }) {
  if (gk) return <div style={{ width: size }}><PlayingCard card={gk} /></div>;
  return (
    <div className="flex flex-col items-center" style={{ width: size }}>
      <div className="kc-slot is-empty" style={{ width: size, aspectRatio: '2/3' }}>
        <div className="flex flex-col items-center justify-center opacity-70">
          <span className="line-label">EMPTY</span>
          <span className="line-label">GOAL</span>
        </div>
      </div>
    </div>
  );
}

export default function JokerSequence({ sequence, onDone }) {
  const [step, setStep] = useState(0);
  const skippingRef = useRef(false);
  const timerRef = useRef(null);
  const donePostedRef = useRef(false);
  // Stabilise onDone so frequent parent re-renders (e.g. server timer
  // broadcasts in online mode) never re-fire the step effect below.
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    if (!sequence) return;
    setStep(0);
    skippingRef.current = false;
    donePostedRef.current = false;
  }, [sequence?.jokerCard?.id, sequence?.followUp?.id]);

  useEffect(() => {
    if (!sequence) return;
    if (step >= TOTAL_STEPS) {
      if (!donePostedRef.current) {
        donePostedRef.current = true;
        onDoneRef.current?.();
      }
      return;
    }
    if (step === 0) sounds.joker();
    if (step === RESULT_STEP) {
      if (sequence.outcome === 'goal') sounds.goal();
      else sounds.save();
    }
    const dur = (step === RESULT_STEP && skippingRef.current) ? (MIN_RESULT_MS + SKIP_LEAD_IN_MS) : DURATIONS[step];
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStep((s) => s + 1), dur);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    // Intentionally exclude `sequence` and `onDone` — sequence object ref
    // stays stable for the lifetime of this reveal (set once in parent),
    // and onDoneRef carries the latest callback without re-triggering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleSkip = () => {
    if (skippingRef.current) return;
    skippingRef.current = true;
    if (step < RESULT_STEP) {
      setStep(RESULT_STEP);
    } else if (step === RESULT_STEP) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setStep((s) => s + 1), MIN_RESULT_MS);
    }
  };

  if (!sequence) return null;

  const isGoal = sequence.outcome === 'goal';
  const resultColor = isGoal ? 'hsl(var(--k-green))' : 'hsl(var(--k-red))';
  const resultShadow = isGoal
    ? '0 0 28px hsl(var(--k-green) / 0.85)'
    : '0 0 28px hsl(var(--k-red) / 0.85)';

  return (
    <div
      onClick={handleSkip}
      role="button"
      tabIndex={-1}
      aria-label="Skip joker reveal"
      data-testid="joker-sequence"
      className="absolute inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center cursor-pointer select-none"
    >
      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="flex flex-col items-center gap-3">
            <div style={{ width: 90 }}><PlayingCard card={sequence.jokerCard} highlight /></div>
            <div className="font-heading text-5xl tracking-[0.18em]" style={{ color: 'hsl(var(--k-gold-bright))', textShadow: '0 0 24px hsl(var(--k-gold) / 0.9)' }}>JOKER!</div>
            <div className="font-body text-xs text-white/65 tracking-[0.3em]">DIRECT SHOT INCOMING</div>
          </motion.div>
        )}
        {step === 1 && (
          <motion.div key="s1" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex flex-col items-center gap-3">
            <div className="font-heading text-sm tracking-[0.3em] text-white/65 uppercase">Shot card</div>
            <div style={{ width: 90 }}><PlayingCard card={sequence.followUp} highlight /></div>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex items-center gap-5">
            <motion.div initial={{ x: -40 }} animate={{ x: 0 }} transition={{ duration: 0.5 }} style={{ width: 78 }}>
              <PlayingCard card={sequence.followUp} highlight />
            </motion.div>
            <div className="font-heading text-3xl text-white/45">VS</div>
            <motion.div initial={{ rotateY: 180, x: 40 }} animate={{ rotateY: 0, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              <GkOrEmpty gk={sequence.gk} size={78} />
            </motion.div>
          </motion.div>
        )}
        {step === 3 && (
          <motion.div
            key="s3"
            data-testid="joker-result-frame"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-5"
          >
            <div className="flex items-center gap-5">
              <div style={{ width: 78 }} data-testid="joker-result-shooter">
                <PlayingCard card={sequence.followUp} highlight />
              </div>
              <div className="font-heading text-2xl text-white/45">VS</div>
              <div data-testid="joker-result-gk">
                <GkOrEmpty gk={sequence.gk} size={78} />
              </div>
            </div>
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
              className="font-heading uppercase"
              data-testid={`joker-result-${sequence.outcome}`}
              style={{
                fontSize: 72,
                letterSpacing: '0.06em',
                color: resultColor,
                textShadow: resultShadow,
                lineHeight: 1,
              }}
            >
              {isGoal ? 'GOAL!' : 'SAVE!'}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
